import { defineStore, storeToRefs } from "pinia"
import { computed, markRaw, ref, shallowRef, triggerRef, watch } from "vue"
import { useToastStore } from "./ToastStore"
import { RBTree } from "@/util/RedBlackTree"
import { timeToFraction } from "@/util/utilFunctions"

export const useSelectionStore = defineStore('selection', () => {

  // --------------------------------------------------------
  // CONSTANTS 
  // --------------------------------------------------------

  const toastStore = useToastStore()

  // --------------------------------------------------------
  // STATE
  // --------------------------------------------------------

  let selectedNotesChart = null
  const selectedNotesChords = shallowRef([])
  const selectedNotesChordMap = shallowRef(new Map())

  // --------------------------------------------------------
  // GETTERS 
  // --------------------------------------------------------

  // --------------------------------------------------------
  // ACTIONS 
  // --------------------------------------------------------

  const clearSelection = () => {
    selectedNotesChordMap.value.clear()
    triggerRef(selectedNotesChordMap)
    selectedNotesChords.value = []
    selectedNotesChart = null
  }


  // notes
  // receives array of arrays of noteIds
  const setSelectedNotes = (chart, targetNotes) => {
    const res = []
    let count = 0
    const notes = chart.notes

    selectedNotesChordMap.value.clear()
    triggerRef(selectedNotesChordMap)

    if (targetNotes.length <= 0) {
      selectedNotesChords.value = []
      selectedNotesChart = null
      return
    }

    const firstNote = notes.value.get(targetNotes[0][0])
    const firstCl = firstNote.eventLine
    const firstTime = firstNote.beat * 60 / firstCl.bpm + firstCl.time

    for (const beatNotes of targetNotes) {
      const beatNote = notes.value.get(beatNotes[0])
      const beatCl = beatNote.eventLine
      const beatTime = beatNote.beat * 60 / beatCl.bpm + beatCl.time
      const r = { time: beatTime - firstTime, notes: [beatNote] }
      selectedNotesChordMap.value.set(beatNote.noteId, r)

      for (let i = 1; i < beatNotes.length; i++) {
        const note = notes.value.get(beatNotes[i])
        r.notes.push(note)
        selectedNotesChordMap.value.set(note.noteId, r)
      }

      count += r.notes.length
      res.push(r)
    }
    selectedNotesChords.value = res
    selectedNotesChart = chart

    toastStore.show(`Selected ${count} notes`)
  }

  function deselectNote(noteId) {
    const chord = selectedNotesChordMap.value.get(noteId)
    if(!chord) return

    selectedNotesChordMap.value.delete(noteId)
    const idx = chord.notes.findIndex(x => x.noteId == noteId)
    if(idx >= 0) chord.notes.splice(idx, 1)

    triggerRef(selectedNotesChordMap)
    triggerRef(selectedNotesChords)
  }

  function deleteSelectedNotes() {
    if (selectedNotesChordMap.value.size <= 0) return
    for (const id of selectedNotesChordMap.value.keys()) {
      selectedNotesChart.removeNote(id)
    }
    selectedNotesChordMap.value.clear()
    triggerRef(selectedNotesChordMap)
    selectedNotesChords.value = []
    selectedNotesChart = null
  }

  function putSelectedNotesAtTargetCell(chart, targetCell) {
    if (selectedNotesChordMap.value.size <= 0) {
      chart.addNote(targetCell.eventLine, targetCell.columnIndex, targetCell.numerator, targetCell.denominator)
      return
    }

    const columnIndexDelta = selectedNotesChords.value[0].notes.sort((x, y) => x.column.columnIndex - y.column.columnIndex)[0].column.columnIndex - targetCell.columnIndex
    const newFirstNoteTime = (targetCell.numerator / targetCell.denominator) * 60 / targetCell.eventLine.bpm + targetCell.eventLine.time

    let currentCl = 0

    for (const b of selectedNotesChords.value) {
      const newNoteTime = b.time + newFirstNoteTime
      if(newNoteTime >= chart.duration.value) continue 

      while (chart.orderedCL.value[currentCl].endTime <= newNoteTime)
        currentCl++

      const eventLine = chart.orderedCL.value[currentCl].eventLine
      const [numerator, denominator] = timeToFraction(eventLine.bpm, newNoteTime - eventLine.time)

      const colIndexes = b.notes.map(n => n.column.columnIndex - columnIndexDelta)
      const colNoteParameters = b.notes.map(n => {
        let { noteId, column, eventLine, numerator, denominator, beat, ...other } = chart.notes.value.get(n.noteId)
        return other
      })
      chart.addChord(eventLine, colIndexes, colNoteParameters, numerator, denominator)
    }
  }

  // --------------------------------------------------------
  // MISC 
  // --------------------------------------------------------

  return {
    clearSelection,
    selectedNotesChordMap,
    selectedNotesChords,
    setSelectedNotes,
    deselectNote,
    deleteSelectedNotes,
    putSelectedNotesAtTargetCell,
  }
})